import React, { useCallback, useEffect, useState } from 'react';
import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  MenuItem,
  LinearProgress,
  linearProgressClasses,
  styled,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { PaymentApiService, PaymentTokenManager } from '../../services/paymentApi';
import { usePlanContext } from '../../contexts/PlanContext';
import { Plan } from '../../hooks/HighLevelContext/types';
import { usePaymentSettings } from '../../contexts/PaymentSettingsContext';
import {
  getDesignerSmartGuildBillingSplit,
  getDesignerSmartGuildDisplayedFeatures,
} from '../../utils/designer-smart-guild-pricing';
import {
  normalizePromoCode,
  type HomeownerPromoResolution,
} from '../../utils/homeowner-promo-codes';
import './styles.scss';

// Styled progress bar matching MatchingProfileSteps
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 5,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: "#4F46E5",
  },
}));

// GraphQL mutations
const PROCESS_PAYMENT = gql`
  mutation ProcessPayment(
    $opaqueDataDescriptor: String
    $opaqueDataValue: String
    $amount: Float!
    $originalAmount: Float
    $email: String!
    $planName: String!
    $planType: String
    $billingCycle: String
    $promoCode: String
    $authorizeNetEnvironment: String
    $billingInfo: BillingInfoInput!
    $homeownerId: ID!
  ) {
    processPayment(
      opaqueDataDescriptor: $opaqueDataDescriptor
      opaqueDataValue: $opaqueDataValue
      amount: $amount
      originalAmount: $originalAmount
      email: $email
      planName: $planName
      planType: $planType
      billingCycle: $billingCycle
      promoCode: $promoCode
      authorizeNetEnvironment: $authorizeNetEnvironment
      billingInfo: $billingInfo
      homeownerId: $homeownerId
    ) {
      success
      transactionId
      message
      subscriptionPlan
    }
  }
`;

interface CustomPaymentProps {
  // Optional props - if not provided, will use plan context
  amount?: number;
  planName?: string;
  planType?: string;
  billingCycle?: string;
  email?: string;
  homeownerId?: string;
  customerInfo?: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    phoneNumber?: string;
  };
  onPaymentSuccess: (response: any) => void;
  onPaymentFailure?: (errorMessage: string) => void;
  // Plan data for integration
  plan?: Plan;
  highlevelProductId?: string;
  highlevelPriceId?: string;
}

interface FormData {
  // Contact Information (Step 1)
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  email: string;
  phoneNumber: string;
  // Payment Information (Step 2)
  cardNumber: string;
  expiry: string;
  cvv: string;
}

const normalizeUsZipCode = (value?: string | null) => {
  if (!value) return '';
  const match = String(value).trim().match(/(\d{5})/);
  return match ? match[1] : '';
};

export const PaymentPage: React.FC<CustomPaymentProps> = ({
  amount: propAmount,
  planName: propPlanName,
  planType: propPlanType,
  billingCycle: propBillingCycle,
  email: propEmail,
  homeownerId: propHomeownerId,
  customerInfo: propCustomerInfo,
  onPaymentSuccess,
  onPaymentFailure,
  plan: propPlan,
  highlevelProductId: propHighlevelProductId,
  highlevelPriceId: propHighlevelPriceId,
}) => {
  
  // Use plan context for data (with fallback if not available)
  let selectedPlan, billingAmount, planType, billingCycle, highlevelProductId, highlevelPriceId, paymentData;
  
  try {
    const planContext = usePlanContext();
    selectedPlan = planContext.selectedPlan;
    billingAmount = planContext.billingAmount;
    planType = planContext.planType;
    billingCycle = planContext.billingCycle;
    highlevelProductId = planContext.highlevelProductId;
    highlevelPriceId = planContext.highlevelPriceId;
    paymentData = planContext.paymentData;
  } catch (error) {
    // Fallback values when PlanProvider is not available
    selectedPlan = null;
    billingAmount = 0;
    planType = '';
    billingCycle = 'Monthly';
    highlevelProductId = null;
    highlevelPriceId = null;
    paymentData = null;
  }
  
  // Use props if provided, otherwise fall back to context
  const amount = propAmount ?? billingAmount;
  const planName = propPlanName ?? selectedPlan?.name ?? 'Guild';
  const planTypeValue = propPlanType ?? planType;
  const billingCycleValue = propBillingCycle ?? billingCycle;
  const email = propEmail ?? paymentData?.email ?? '';
  const homeownerId = propHomeownerId ?? paymentData?.homeownerId ?? '';
  const customerInfo = propCustomerInfo ?? paymentData?.customerInfo ?? {
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phoneNumber: ''
  };
  const normalizedCustomerZipCode = normalizeUsZipCode(customerInfo.zipCode);
  const plan = propPlan ?? selectedPlan;
  const highlevelProductIdValue =
    propHighlevelProductId ?? highlevelProductId ?? plan?.highlevelProductId ?? null;
  const highlevelPriceIdValue =
    propHighlevelPriceId ?? highlevelPriceId ?? plan?.highlevelPriceId ?? null;
  
  // Get payment settings from context to determine test mode
  const { isTestMode } = usePaymentSettings();

  const resolveAuthorizeNetEnvironment = () => {
    const rawEnv = (process.env.REACT_APP_AUTHORIZE_NET_ENVIRONMENT || '').toLowerCase().trim();
    if (rawEnv) {
      if (['production', 'prod', 'live'].includes(rawEnv)) {
        return 'production';
      }
      if (['sandbox', 'test', 'testing'].includes(rawEnv)) {
        return 'sandbox';
      }
    }
    return isTestMode() ? 'sandbox' : 'production';
  };

  const authorizeNetEnvironment = resolveAuthorizeNetEnvironment();
  const useSandbox = authorizeNetEnvironment === 'sandbox';




  const [formData, setFormData] = useState<FormData>({
    // Contact Information
    firstName: customerInfo.firstName || '',
    lastName: customerInfo.lastName || '',
    address: customerInfo.address || '',
    city: customerInfo.city || '',
    state: customerInfo.state || '',
    zipCode: normalizedCustomerZipCode,
    country: 'United States',
    email: email || '',
    phoneNumber: customerInfo.phoneNumber || '',
    // Payment Information
    cardNumber: '',
    expiry: '',
    cvv: '',
  });

  // Update form data when props change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      firstName: customerInfo.firstName || prev.firstName,
      lastName: customerInfo.lastName || prev.lastName,
      address: customerInfo.address || prev.address,
      city: customerInfo.city || prev.city,
      state: customerInfo.state || prev.state,
      zipCode: normalizedCustomerZipCode || prev.zipCode,
      email: email || prev.email,
      phoneNumber: customerInfo.phoneNumber || prev.phoneNumber,
    }));
  }, [customerInfo.firstName, customerInfo.lastName, customerInfo.address, customerInfo.city, customerInfo.state, customerInfo.phoneNumber, email, normalizedCustomerZipCode]);

  const [paymentStep, setPaymentStep] = useState<1 | 2>(1); // 1 = Contact Info, 2 = Payment Details
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoError, setPromoError] = useState<string | null>(null);
  const [appliedPromo, setAppliedPromo] = useState<HomeownerPromoResolution | null>(null);
  const [isPromoLoading, setIsPromoLoading] = useState(false);
  const [processPaymentMutation] = useMutation(PROCESS_PAYMENT);

  // Load Accept.js script on component mount
  useEffect(() => {
    const loadAcceptJs = () => {
      const scriptId = 'authorize-net-acceptjs';
      const desiredEnv = useSandbox ? 'sandbox' : 'production';
      const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
      const existingEnv = existingScript?.getAttribute('data-env');

      if (existingScript && existingEnv && existingEnv !== desiredEnv) {
        existingScript.remove();
        if ((window as any).Accept) {
          delete (window as any).Accept;
        }
      }

      if (!window.Accept) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.setAttribute('data-env', desiredEnv);
        script.src = useSandbox 
          ? 'https://jstest.authorize.net/v1/Accept.js'  // Sandbox URL
          : 'https://js.authorize.net/v1/Accept.js';      // Production URL
        script.async = true;
        script.onload = () => {
        };
        script.onerror = () => {
        };
        document.head.appendChild(script);
      }
    };

    loadAcceptJs();
  }, [useSandbox]);

  const detectCardType = (number: string): 'Visa' | 'Mastercard' | 'Amex' | 'Discover' | null => {
    const cleaned = number.replace(/\s/g, '');
    
    if (/^4/.test(cleaned)) return 'Visa';
    if (/^5[1-5]/.test(cleaned)) return 'Mastercard';
    if (/^3[47]/.test(cleaned)) return 'Amex';
    if (/^6(?:011|5)/.test(cleaned)) return 'Discover';
    
    return null;
  };

  const cardType = detectCardType(formData.cardNumber);

  const CardLogo = ({ type }: { type: 'Visa' | 'Mastercard' | 'Amex' | 'Discover' | null }) => {
    if (!type) return null;
    
    return (
      <PaymentIcon 
        type={type} 
        width={40} 
        height={24}
      />
    );
  };

  const handleInputChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;

    // Format card number with spaces
    if (field === 'cardNumber') {
      value = value.replace(/\s/g, '').replace(/(\d{4})(?=\d)/g, '$1 ');
      if (value.length > 19) value = value.substring(0, 19);
    }
    
    // Format expiry date
    if (field === 'expiry') {
      value = value.replace(/\D/g, '');
      if (value.length >= 2) {
        value = value.substring(0, 2) + '/' + value.substring(2, 4);
      }
      if (value.length > 5) value = value.substring(0, 5);
    }

    // Format CVV (max 4 digits for Amex)
    if (field === 'cvv') {
      value = value.replace(/\D/g, '');
      const maxLength = cardType === 'Amex' ? 4 : 3;
      if (value.length > maxLength) value = value.substring(0, maxLength);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateContactInfo = (): boolean => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.address.trim()) {
      setError('Address is required');
      return false;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return false;
    }
    if (!formData.state.trim()) {
      setError('State is required');
      return false;
    }
    if (!formData.zipCode.trim()) {
      setError('Zip code is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      setError('Phone number is required');
      return false;
    }
    return true;
  };

  const validatePaymentInfo = (): boolean => {
    if (!formData.cardNumber.replace(/\s/g, '')) {
      setError('Card number is required');
      return false;
    }
    if (!formData.expiry) {
      setError('Expiry date is required');
      return false;
    }
    if (!formData.cvv) {
      setError('CVV is required');
      return false;
    }
    return true;
  };

  const handleContinueToPayment = () => {
    setError(null);
    if (validateContactInfo()) {
      if (requiresPaymentDetails) {
        setPaymentStep(2);
        return;
      }

      void handleSubmit({ skipPaymentDetails: true });
    }
  };

  const handleBackToContact = () => {
    setPaymentStep(1);
    setError(null);
  };

  const handleSubmit = async (options?: { skipPaymentDetails?: boolean }) => {
    setError(null);
    
    if (requiresPaymentDetails && !options?.skipPaymentDetails && !validatePaymentInfo()) {
      return;
    }

    setIsProcessing(true);

    try {
      let opaqueData: { dataDescriptor?: string; dataValue?: string } | null = null;

      if (requiresPaymentDetails) {
        // Ensure Accept.js is loaded
        if (!window.Accept || !window.Accept.dispatchData) {
          throw new Error('Accept.js is not loaded correctly. Please refresh the page and try again.');
        }

        // Use environment-driven test mode to align with backend configuration
        const clientKey = useSandbox
          ? (process.env.REACT_APP_AUTHORIZE_NET_PUBLIC_CLIENT_KEY_SANDBOX || process.env.REACT_APP_AUTHORIZE_NET_PUBLIC_CLIENT_KEY || '')
          : (process.env.REACT_APP_AUTHORIZE_NET_PUBLIC_CLIENT_KEY_PRODUCTION || process.env.REACT_APP_AUTHORIZE_NET_PUBLIC_CLIENT_KEY || '');
        
        const apiLoginID = useSandbox
          ? (process.env.REACT_APP_AUTHORIZE_NET_API_LOGIN_ID_SANDBOX || process.env.REACT_APP_AUTHORIZE_NET_API_LOGIN_ID || '')
          : (process.env.REACT_APP_AUTHORIZE_NET_API_LOGIN_ID_PRODUCTION || process.env.REACT_APP_AUTHORIZE_NET_API_LOGIN_ID || '');

        if (!clientKey || !apiLoginID) {
          throw new Error('Authorize.Net credentials are not configured. Please add them to your environment variables.');
        }

        // Prepare card data for Accept.js
        const [expMonth, expYear] = formData.expiry.split('/');
        const fullYear = expYear.length === 2 ? `20${expYear}` : expYear;

        const secureData = {
          authData: {
            clientKey: clientKey,
            apiLoginID: apiLoginID,
          },
          cardData: {
            cardNumber: formData.cardNumber.replace(/\s/g, ''),
            month: expMonth,
            year: fullYear,
            cardCode: formData.cvv,
          },
        };

        // Get opaque data from Accept.js
        opaqueData = await new Promise<any>((resolve, reject) => {
          try {
            window.Accept.dispatchData(secureData, (response: any) => {
              try {
                if (response && response.messages && response.messages.resultCode === 'Ok') {
                  resolve(response.opaqueData);
                } else {
                  const errorMessage = response?.messages?.message?.[0]?.text || 'Unknown Accept.js error';
                  reject(new Error(errorMessage));
                }
              } catch (callbackError) {
                reject(new Error('Payment processing failed'));
              }
            });
          } catch (dispatchError) {
            reject(new Error('Payment processing failed'));
          }
        });
      }


      // Process payment with our backend
      const { data } = await processPaymentMutation({
        variables: {
          opaqueDataDescriptor: opaqueData?.dataDescriptor ?? null,
          opaqueDataValue: opaqueData?.dataValue ?? null,
          amount: resolvedAmount,
          originalAmount: amount,
          email: formData.email,
          planName: planName,
          planType: planTypeValue || null,
          billingCycle: billingCycleValue || null,
          promoCode: appliedPromo?.code || null,
          authorizeNetEnvironment,
          billingInfo: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
            phoneNumber: formData.phoneNumber,
            email: formData.email,
          },
          homeownerId: homeownerId,
        },
      });

      if (data.processPayment.success) {
        // Store payment data for confirmation page (including billing address)
        const paymentData = {
          transactionId: data.processPayment.transactionId,
          planName: planName,
          planType: planTypeValue,
          amount: resolvedAmount,
          originalAmount: amount,
          email: formData.email,
          timestamp: new Date().toISOString(),
          customerInfo: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
            phoneNumber: formData.phoneNumber,
          },
          homeownerId: homeownerId,
          highlevelProductId: resolvedHighlevelProductId,
          highlevelPriceId: resolvedHighlevelPriceId,
          billingCycle: billingCycleValue,
          promoCode: appliedPromo?.code || null,
          promoLabel: appliedPromo?.label || null,
          savingsAmount: appliedPromo?.savingsAmount ?? 0,
        };
        
        PaymentTokenManager.storePaymentData(paymentData);
        
        onPaymentSuccess(data.processPayment);
      } else {
        throw new Error(data.processPayment.message || 'Payment failed');
      }

    } catch (err: any) {
      setError(err.message || 'Payment processing failed. Please try again.');
      onPaymentFailure?.(err.message || 'Payment processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatPrice = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const normalizedPlanToken = String(planTypeValue || planName || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  const isGuildPlusPlan =
    normalizedPlanToken.includes('guildplus') || normalizedPlanToken.includes('guild+');
  const isGuildPlan = normalizedPlanToken.includes('guild') && !isGuildPlusPlan;
  const isMonthlyCycle = String(billingCycleValue || '')
    .trim()
    .toLowerCase()
    .includes('month');
  const resolvedAmount = appliedPromo?.finalAmount ?? amount;
  const requiresPaymentDetails = resolvedAmount > 0;
  const resolvedHighlevelProductId =
    appliedPromo?.highlevelProductId || highlevelProductIdValue;
  const resolvedHighlevelPriceId =
    appliedPromo?.highlevelPriceId || highlevelPriceIdValue;
  const showGuildTrialMessage = isGuildPlan && isMonthlyCycle;
  const designerSmartGuildSplit = getDesignerSmartGuildBillingSplit(plan);
  const isDesignerSmartGuildDueNowCheckout =
    !!designerSmartGuildSplit &&
    Number(resolvedAmount) === Number(designerSmartGuildSplit.dueNowAmount);
  const billingChipLabel = isDesignerSmartGuildDueNowCheckout
    ? 'Due Now'
    : billingCycleValue;
  const appliedPromoCode = appliedPromo?.code ?? null;

  const resolvePromoCode = useCallback(
    async (promoCodeValue: string) => {
      if (!homeownerId) {
        throw new Error('Membership user is required before applying a promo code.');
      }

      return PaymentApiService.getInstance().resolveHomeownerPromo({
        homeownerId,
        promoCode: promoCodeValue,
        planType: planTypeValue || null,
        planName,
        originalAmount: amount,
      });
    },
    [amount, homeownerId, planName, planTypeValue]
  );

  useEffect(() => {
    if (!appliedPromoCode) {
      return;
    }

    let isActive = true;

    setIsPromoLoading(true);

    void resolvePromoCode(appliedPromoCode)
      .then((refreshedPromo) => {
        if (!isActive) {
          return;
        }

        setAppliedPromo(refreshedPromo);
        setPromoError(null);
      })
      .catch((promoValidationError: any) => {
        if (!isActive) {
          return;
        }

        setAppliedPromo(null);
        setPromoError(promoValidationError.message || 'Promo code could not be applied.');
      })
      .finally(() => {
        if (isActive) {
          setIsPromoLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [appliedPromoCode, resolvePromoCode]);

  const handlePromoCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setPromoCodeInput(nextValue);
    setPromoError(null);

    if (appliedPromo && normalizePromoCode(nextValue) !== appliedPromo.normalizedCode) {
      setAppliedPromo(null);
    }
  };

  const handleApplyPromoCode = async () => {
    setPromoError(null);
    setIsPromoLoading(true);

    try {
      const resolvedPromo = await resolvePromoCode(promoCodeInput);

      if (!resolvedPromo) {
        setAppliedPromo(null);
        return;
      }

      setAppliedPromo(resolvedPromo);
      setPromoCodeInput(resolvedPromo.code);
      setError(null);
    } catch (promoApplyError: any) {
      setAppliedPromo(null);
      setPromoError(promoApplyError.message || 'Promo code could not be applied.');
    } finally {
      setIsPromoLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    setAppliedPromo(null);
    setPromoError(null);
    setPromoCodeInput('');
  };

  const getFeatures = () => {
    
    // Try to extract features from various sources
    let planFeatures: string[] = [];
    
    if (!plan) {
      return ['Plan features loading...'];
    }
    
    // Use the same logic as membership cards: plan?.perks?.values || plan?.benefits || []
    if (typeof plan.perks?.values === 'function') {
      try {
        const values = Array.from(plan.perks.values());
        if (Array.isArray(values) && values.length > 0) {
          planFeatures = values;
        }
      } catch (error) {
      }
    }
    
    // If no features from perks.values, try benefits
    if (!planFeatures || planFeatures.length === 0) {
      if (Array.isArray(plan.benefits) && plan.benefits.length > 0) {
        planFeatures = plan.benefits;
      }
    }
    
    // If still no features, try metadata.features
    if (!planFeatures || planFeatures.length === 0) {
      if (Array.isArray(plan.metadata?.features) && plan.metadata?.features && plan.metadata.features.length > 0) {
        planFeatures = plan.metadata.features;
      }
    }
    
    // If still no features, extract from HTML description
    if (!planFeatures || planFeatures.length === 0) {
      if (plan.description) {
        planFeatures = extractFeaturesFromDescription(plan.description);
      }
    }
    
    const displayedPlanFeatures = getDesignerSmartGuildDisplayedFeatures(
      plan,
      planFeatures
    );

    if (displayedPlanFeatures.length > 0) {
      return displayedPlanFeatures;
    }

    if (planFeatures && planFeatures.length > 0) {
      return planFeatures;
    }
    
    // If no plan data available, show error message instead of hardcoded features
    return ['Plan features loading...'];
  };

  // Helper function to extract features from HTML description
  const extractFeaturesFromDescription = (description: string): string[] => {
    try {
      
      // Create a temporary DOM element to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = description;
      
      // Find all list items in the description
      const listItems = tempDiv.querySelectorAll('li');
      
      const features: string[] = [];
      
      listItems.forEach((li, index) => {
        const text = li.textContent?.trim();
        if (text) {
          features.push(text);
        }
      });
      
      return features;
    } catch (error) {
      return [];
    }
  };

  return (
    <Box className="payment-page">
      <Box className="payment-container">
        <Box className="payment-grid">
          {/* Left Column - Membership Details */}
          <Box className="membership-section">
            {/* Logo */}
            <Box className="logo-section">
              <img 
                src="https://msgsndr-private.storage.googleapis.com/locationPhotos/46cc900c-dc33-4614-b6a7-69c28f1ab026.png" 
                alt="Home Design Guild" 
                className="logo"
              />
            </Box>

            {/* Membership Card */}
            <Card className="membership-card">
              <CardContent>
                <Typography variant="h4" className="membership-title">
                  Join The {planName}
                </Typography>
                
                <Box className="price-section">
                  <Box>
                    {appliedPromo ? (
                      <Typography
                        variant="body2"
                        sx={{ color: '#6B7280', textDecoration: 'line-through' }}
                      >
                        ${formatPrice(amount)}
                      </Typography>
                    ) : null}
                    <Typography variant="h5" className="price">
                      {resolvedAmount === 0 ? 'Complimentary' : `$${formatPrice(resolvedAmount)}`}
                    </Typography>
                  </Box>
                  <Chip 
                    label={billingChipLabel} 
                    className="billing-chip"
                    size="small"
                  />
                </Box>
                {appliedPromo ? (
                  <Typography
                    variant="body2"
                    sx={{ color: '#00A06D', fontWeight: 600, mb: 1 }}
                  >
                    {appliedPromo.label} applied. You saved ${formatPrice(appliedPromo.savingsAmount)}.
                  </Typography>
                ) : null}
                {showGuildTrialMessage ? (
                  <Typography
                    variant="body2"
                    sx={{ color: '#4F46E5', fontWeight: 600, mb: 1 }}
                  >
                    7-day free trial. First charge starts after your trial.
                  </Typography>
                ) : null}

                <Divider className="divider" />

                <Typography variant="h6" className="features-title">
                  What's Included:
                </Typography>
                <Box className="features-list">
                  {getFeatures().map((feature: string, index: number) => (
                    <Box key={index} className="feature-item">
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="#00A06D"
                        className="check-icon"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z" />
                      </svg>
                      <Typography variant="body2" className="feature-text">
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Right Column - Payment Form (2-Step) */}
          <Box className="payment-section">
            <Card className="payment-card">
              <CardContent>
                {/* Step Indicator - Matching Profile Style */}
                <Box sx={{ mb: 3 }}>
                  <Box position="relative">
                    {/* Desktop view */}
                    <Box sx={{ display: { xs: "none", sm: "block" } }}>
                      <Stack direction="row">
                        {[
                          { title: "Contact Information" },
                          { title: "Payment Details" }
                        ].map((step, index) => (
                          <Box
                            key={index}
                            width="100%"
                            textAlign="center"
                            position="relative"
                            zIndex={5}
                          >
                            <Box
                              sx={{
                                borderRadius: "50%",
                                background: paymentStep > index + 1 ? "#4F46E5" : "#fff",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: 40,
                                height: 40,
                                mx: "auto",
                                border: "1px solid",
                                borderColor: paymentStep === index + 1 ? "#4F46E5" : "#E5E7EB",
                              }}
                            >
                              {paymentStep > index + 1 ? (
                                <CheckIcon sx={{ color: "#fff", fontSize: 20 }} />
                              ) : (
                                <Typography
                                  sx={{
                                    color: paymentStep === index + 1 ? "#4F46E5" : "#9CA3AF",
                                    fontWeight: 600,
                                    fontSize: '14px'
                                  }}
                                >
                                  {index + 1}
                                </Typography>
                              )}
                            </Box>
                            <Typography
                              sx={{
                                position: "absolute",
                                bottom: 0,
                                left: "50%",
                                transform: "translate(-50%, 100%)",
                                whiteSpace: "nowrap",
                                fontSize: '14px',
                                mt: 1
                              }}
                            >
                              {step.title}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>

                    {/* Mobile view */}
                    <Box sx={{ display: { xs: "block", sm: "none" } }}>
                      <Stack direction="row">
                        {[
                          { title: "Contact Information" },
                          { title: "Payment Details" }
                        ].map((step, index) => (
                          <Box
                            key={index}
                            width="100%"
                            textAlign="center"
                            position="relative"
                            zIndex={5}
                          >
                            <Box
                              sx={{
                                borderRadius: "50%",
                                background: paymentStep > index + 1 ? "#4F46E5" : "#fff",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: 28,
                                height: 28,
                                mx: "auto",
                                border: "1px solid",
                                borderColor: paymentStep === index + 1 ? "#4F46E5" : "#E5E7EB",
                              }}
                            >
                              {paymentStep > index + 1 ? (
                                <CheckIcon sx={{ color: "#fff", fontSize: 16 }} />
                              ) : (
                                <Typography
                                  sx={{
                                    color: paymentStep === index + 1 ? "#4F46E5" : "#9CA3AF",
                                    fontWeight: 600,
                                    fontSize: '12px'
                                  }}
                                >
                                  {index + 1}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>

                      <Box
                        sx={{
                          position: "absolute",
                          top: "100%",
                          left: "50%",
                          transform: "translateX(-50%)",
                          textAlign: "center",
                          mt: 1
                        }}
                      >
                        <Typography sx={{ fontSize: '14px' }}>
                          {paymentStep === 1 ? 'Contact Information' : 'Payment Details'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Progress bar */}
                    <BorderLinearProgress
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: 0,
                        right: 0,
                        transform: "translateY(-50%)",
                      }}
                      value={paymentStep === 2 ? 100 : 25}
                      variant="determinate"
                    />
                  </Box>
                </Box>

                {error && (
                  <Alert severity="error" className="error-alert">
                    {error}
                  </Alert>
                )}

                {/* Spacer between stepper and form */}
                <Box sx={{ height: 24 }} />

                {/* Step 1: Contact Information */}
                {paymentStep === 1 && (
                  <Box className="form-section">
                    <Stack 
                      direction={{ xs: 'column', sm: 'row' }} 
                      spacing={2} 
                      sx={{ mb: 2 }}
                    >
                      <TextField
                        fullWidth
                        label="First Name"
                        required
                        value={formData.firstName}
                        onChange={handleInputChange('firstName')}
                        inputProps={{
                          autoComplete: 'given-name',
                        }}
                        error={!!error && !formData.firstName}
                      />
                      <TextField
                        fullWidth
                        label="Last Name"
                        required
                        value={formData.lastName}
                        onChange={handleInputChange('lastName')}
                        inputProps={{
                          autoComplete: 'family-name',
                        }}
                        error={!!error && !formData.lastName}
                      />
                    </Stack>

                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Address"
                        required
                        value={formData.address}
                        onChange={handleInputChange('address')}
                        inputProps={{
                          autoComplete: 'street-address',
                        }}
                        error={!!error && !formData.address}
                      />
                    </Box>

                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label="City"
                        required
                        value={formData.city}
                        onChange={handleInputChange('city')}
                        inputProps={{
                          autoComplete: 'address-level2',
                        }}
                        error={!!error && !formData.city}
                      />
                      <TextField
                        select
                        fullWidth
                        label="State"
                        required
                        value={formData.state}
                        onChange={handleInputChange('state')}
                        error={!!error && !formData.state}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: 400, // 10 items * ~40px each
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="AL">Alabama</MenuItem>
                        <MenuItem value="AK">Alaska</MenuItem>
                        <MenuItem value="AZ">Arizona</MenuItem>
                        <MenuItem value="AR">Arkansas</MenuItem>
                        <MenuItem value="CA">California</MenuItem>
                        <MenuItem value="CO">Colorado</MenuItem>
                        <MenuItem value="CT">Connecticut</MenuItem>
                        <MenuItem value="DE">Delaware</MenuItem>
                        <MenuItem value="FL">Florida</MenuItem>
                        <MenuItem value="GA">Georgia</MenuItem>
                        <MenuItem value="HI">Hawaii</MenuItem>
                        <MenuItem value="ID">Idaho</MenuItem>
                        <MenuItem value="IL">Illinois</MenuItem>
                        <MenuItem value="IN">Indiana</MenuItem>
                        <MenuItem value="IA">Iowa</MenuItem>
                        <MenuItem value="KS">Kansas</MenuItem>
                        <MenuItem value="KY">Kentucky</MenuItem>
                        <MenuItem value="LA">Louisiana</MenuItem>
                        <MenuItem value="ME">Maine</MenuItem>
                        <MenuItem value="MD">Maryland</MenuItem>
                        <MenuItem value="MA">Massachusetts</MenuItem>
                        <MenuItem value="MI">Michigan</MenuItem>
                        <MenuItem value="MN">Minnesota</MenuItem>
                        <MenuItem value="MS">Mississippi</MenuItem>
                        <MenuItem value="MO">Missouri</MenuItem>
                        <MenuItem value="MT">Montana</MenuItem>
                        <MenuItem value="NE">Nebraska</MenuItem>
                        <MenuItem value="NV">Nevada</MenuItem>
                        <MenuItem value="NH">New Hampshire</MenuItem>
                        <MenuItem value="NJ">New Jersey</MenuItem>
                        <MenuItem value="NM">New Mexico</MenuItem>
                        <MenuItem value="NY">New York</MenuItem>
                        <MenuItem value="NC">North Carolina</MenuItem>
                        <MenuItem value="ND">North Dakota</MenuItem>
                        <MenuItem value="OH">Ohio</MenuItem>
                        <MenuItem value="OK">Oklahoma</MenuItem>
                        <MenuItem value="OR">Oregon</MenuItem>
                        <MenuItem value="PA">Pennsylvania</MenuItem>
                        <MenuItem value="RI">Rhode Island</MenuItem>
                        <MenuItem value="SC">South Carolina</MenuItem>
                        <MenuItem value="SD">South Dakota</MenuItem>
                        <MenuItem value="TN">Tennessee</MenuItem>
                        <MenuItem value="TX">Texas</MenuItem>
                        <MenuItem value="UT">Utah</MenuItem>
                        <MenuItem value="VT">Vermont</MenuItem>
                        <MenuItem value="VA">Virginia</MenuItem>
                        <MenuItem value="WA">Washington</MenuItem>
                        <MenuItem value="WV">West Virginia</MenuItem>
                        <MenuItem value="WI">Wisconsin</MenuItem>
                        <MenuItem value="WY">Wyoming</MenuItem>
                      </TextField>
                    </Stack>

                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Zip Code"
                        required
                        value={formData.zipCode}
                        onChange={handleInputChange('zipCode')}
                        inputProps={{
                          autoComplete: 'postal-code',
                        }}
                        error={!!error && !formData.zipCode}
                      />
                      <TextField
                        fullWidth
                        label="Country"
                        required
                        value={formData.country}
                        onChange={handleInputChange('country')}
                        disabled
                        inputProps={{
                          autoComplete: 'country-name',
                        }}
                      />
                    </Stack>

                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        required
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        inputProps={{
                          autoComplete: 'email',
                        }}
                        error={!!error && !formData.email}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        required
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={handleInputChange('phoneNumber')}
                        inputProps={{
                          autoComplete: 'tel',
                        }}
                        error={!!error && !formData.phoneNumber}
                      />
                    </Box>

                    {isGuildPlusPlan ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 700 }}
                        >
                          Promo code
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <TextField
                            fullWidth
                            label="Promo Code"
                            value={promoCodeInput}
                            onChange={handlePromoCodeChange}
                            placeholder="Enter promo code"
                            disabled={isPromoLoading}
                          />
                          <Button
                            variant="outlined"
                            onClick={() => {
                              if (appliedPromo) {
                                handleRemovePromoCode();
                                return;
                              }

                              void handleApplyPromoCode();
                            }}
                            disabled={isPromoLoading}
                            sx={{ minWidth: { sm: 132 } }}
                          >
                            {isPromoLoading ? 'Applying...' : appliedPromo ? 'Remove' : 'Apply'}
                          </Button>
                        </Stack>
                        {promoError ? (
                          <Alert severity="error" sx={{ mt: 1.5 }}>
                            {promoError}
                          </Alert>
                        ) : null}
                        {appliedPromo ? (
                          <Alert severity="success" sx={{ mt: 1.5 }}>
                            {appliedPromo.finalAmount === 0
                              ? 'Complimentary membership activated. No card will be required.'
                              : `Promo applied. New total: $${formatPrice(appliedPromo.finalAmount)}.`}
                          </Alert>
                        ) : null}
                      </Box>
                    ) : null}

                    {/* Continue Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleContinueToPayment}
                      disabled={isPromoLoading}
                      size="large"
                      sx={{ 
                        mt: 3,
                        py: 1.5,
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        borderRadius: '8px',
                        backgroundColor: '#000000',
                        '&:hover': {
                          backgroundColor: '#1a1a1a',
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        },
                        '&:active': {
                          transform: 'translateY(0px)',
                        },
                        '&:disabled': {
                          backgroundColor: '#E5E7EB',
                          color: '#9CA3AF',
                        },
                      }}
                    >
                      {requiresPaymentDetails
                        ? 'Continue'
                        : 'Activate Complimentary Membership'}
                    </Button>
                  </Box>
                )}

                {/* Step 2: Payment Details */}
                {paymentStep === 2 && (
                  <Box className="form-section">
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Card Number"
                        required
                        value={formData.cardNumber}
                        onChange={handleInputChange('cardNumber')}
                        placeholder="1234 5678 9012 3456"
                        inputProps={{
                          maxLength: 19,
                          inputMode: 'numeric',
                          autoComplete: 'cc-number',
                        }}
                        error={!!error && !formData.cardNumber}
                      />
                      {cardType && (
                        <Box sx={{ 
                          position: 'absolute', 
                          right: 12, 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none'
                        }}>
                          <CardLogo type={cardType} />
                        </Box>
                      )}
                    </Box>

                    <Stack 
                      direction={{ xs: 'column', sm: 'row' }} 
                      spacing={2} 
                      sx={{ mb: 2 }}
                    >
                      <TextField
                        fullWidth
                        label="Exp. Date"
                        required
                        value={formData.expiry}
                        onChange={handleInputChange('expiry')}
                        placeholder="MM/YY"
                        inputProps={{
                          maxLength: 5,
                          inputMode: 'numeric',
                          autoComplete: 'cc-exp',
                        }}
                        error={!!error && !formData.expiry}
                      />
                      <TextField
                        fullWidth
                        label="CVV"
                        required
                        value={formData.cvv}
                        onChange={handleInputChange('cvv')}
                        placeholder="123"
                        inputProps={{
                          maxLength: 4,
                          inputMode: 'numeric',
                          autoComplete: 'cc-csc',
                        }}
                        error={!!error && !formData.cvv}
                        helperText="3-digit security code on back of card"
                      />
                    </Stack>

                    {/* Spacer to match Step 1 height */}
                    <Box sx={{ height: 200 }} />

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={handleBackToContact}
                        size="large"
                        sx={{ 
                          flex: '0 0 auto',
                          minWidth: '80px',
                          py: 1.5,
                          fontWeight: 'bold',
                          fontSize: '0.9rem',
                          borderRadius: '8px',
                          borderColor: '#000000',
                          color: '#000000',
                          '&:hover': {
                            borderColor: '#1a1a1a',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          void handleSubmit();
                        }}
                        disabled={isProcessing || (requiresPaymentDetails && !window.Accept)}
                        size="large"
                        sx={{ 
                          flex: 1,
                          py: 1.5,
                          fontWeight: 'bold',
                          fontSize: '0.95rem',
                          borderRadius: '8px',
                          backgroundColor: '#000000',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            backgroundColor: '#1a1a1a',
                            transform: 'translateY(-2px)',
                            boxShadow: 3,
                          },
                          '&:active': {
                            transform: 'translateY(0px)',
                          },
                          '&:disabled': {
                            backgroundColor: '#E5E7EB',
                            color: '#9CA3AF',
                          },
                        }}
                      >
                        {isProcessing ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} sx={{ color: 'white' }} />
                            <span>Processing...</span>
                          </Box>
                        ) : (
                          showGuildTrialMessage
                            ? 'Start 7-Day Free Trial'
                            : `Complete Payment - $${formatPrice(resolvedAmount)}`
                        )}
                      </Button>
                    </Stack>
                  </Box>
                )}

                {/* Security Note */}
                <Box className="security-note">
                  {requiresPaymentDetails ? (
                    <svg className="security-icon" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  ) : null}
                  <Typography variant="caption" className="security-text">
                    {requiresPaymentDetails
                      ? 'Secure payment processing by Authorize.Net'
                      : 'No card is required for this complimentary membership.'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Extend window interface for Accept.js
declare global {
  interface Window {
    Accept: any;
  }
}

export default PaymentPage;
